import { useState, useEffect } from 'react';
import { signInWithGoogle, signInWithMicrosoft, loginErrorMessage } from '@elka/firebase';
import { auth } from '@/lib/firebase/config';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { LoginPage as LoginPageUI } from '@elka/ui';

// Flag stays off until the Azure app registration + Firebase provider are configured
// (docs/features/MICROSOFT_LOGIN.md); until then the Microsoft button is hidden.
const MICROSOFT_LOGIN_ENABLED = import.meta.env.VITE_ENABLE_MICROSOFT_LOGIN === 'true';

export function LoginPage() {
  const currentYear = new Date().getFullYear();
  const { user, isAuthorized } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Navigate when the user is authorized. Unprovisioned users (authenticated but
  // no tenant claim) are NOT signed out here — ProtectedRoute shows them the
  // NotProvisionedScreen instead.
  useEffect(() => {
    if (user && isAuthorized) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, isAuthorized, navigate]);

  const handleSignIn = async (signIn: (a: typeof auth) => Promise<unknown>) => {
    setIsLoading(true);
    setError(null);
    try {
      await signIn(auth);
      // Authorization + routing happen in the effect above.
    } catch (err: unknown) {
      console.error('Login error:', err);
      setError(loginErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LoginPageUI
      calm
      eyebrow="אלקה · CRM"
      title="כניסה לצוות מכירות"
      subtitle="ניהול קשרי לקוחות, צנרת עסקאות ומשימות"
      brandTitle="SALES OS"
      valueProps={['צנרת מכירות שקופה.', 'כל ליד, כל עסקה,', 'כל צעד הבא — במקום אחד.']}
      onGoogleSignIn={() => handleSignIn(signInWithGoogle)}
      onMicrosoftSignIn={
        MICROSOFT_LOGIN_ENABLED ? () => handleSignIn(signInWithMicrosoft) : undefined
      }
      isLoading={isLoading}
      error={error}
      footerText={`© ${currentYear} CRM אלקה | כל הזכויות שמורות`}
    />
  );
}
